import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo, forwardRef } from 'react';
import { BankIcon, CausesIcon, MerchantIcon, UserProfileIcon, BillPaymentIcon, RecurrentIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles, getColor, getColorFamily, Shimmer } from '@paypalcorp/pp-react-utils';
var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var _textStyles = textStyles(tokens),
    caption = _textStyles.caption,
    body = _textStyles.body,
    headingSm = _textStyles.headingSm,
    headingLg = _textStyles.headingLg;
  var _utilStyles = utilStyles(tokens),
    shimmerStyles = _utilStyles.shimmerStyles;
  var sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
    sysSizeCircleSm = tokens.sysSizeCircleSm,
    sysSizeCircleMd = tokens.sysSizeCircleMd,
    sysSizeCircleLg = tokens.sysSizeCircleLg,
    sysSizeCircleXl = tokens.sysSizeCircleXl,
    sysColorAccent1Contrast = tokens.sysColorAccent1Contrast,
    sysColorAccent2Contrast = tokens.sysColorAccent2Contrast,
    sysColorAccent3Contrast = tokens.sysColorAccent3Contrast,
    sysColorAccent4Contrast = tokens.sysColorAccent4Contrast,
    sysColorBackgroundMain = tokens.sysColorBackgroundMain,
    sysColorAccent1Main = tokens.sysColorAccent1Main,
    sysColorAccent2Main = tokens.sysColorAccent2Main,
    sysColorAccent3Main = tokens.sysColorAccent3Main,
    sysColorAccent4Main = tokens.sysColorAccent4Main,
    sysBorderThicknessNormal = tokens.sysBorderThicknessNormal; // Accepted colors, keep this order to match the sequence for the initials

  var acceptedColors = {
    sysColorAccent4Main: sysColorAccent4Main,
    sysColorAccent2Main: sysColorAccent2Main,
    sysColorAccent1Main: sysColorAccent1Main,
    sysColorAccent3Main: sysColorAccent3Main
  }; // legal colors for getting getColorFamily

  var legalColors = {
    sysColorAccent1Main: sysColorAccent1Main,
    sysColorAccent2Main: sysColorAccent2Main,
    sysColorAccent3Main: sysColorAccent3Main,
    sysColorAccent4Main: sysColorAccent4Main,
    sysColorAccent1Contrast: sysColorAccent1Contrast,
    sysColorAccent2Contrast: sysColorAccent2Contrast,
    sysColorAccent3Contrast: sysColorAccent3Contrast,
    sysColorAccent4Contrast: sysColorAccent4Contrast
  };
  var sizeXs = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: size_xs;\n    width: ", ";\n    height: ", ";\n    ", ";\n  "])), sysSizeCircleSm, sysSizeCircleSm, caption({
    lineHeight: sysSizeCircleSm
  }));
  var sizeSm = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: size_sm;\n    width: ", ";\n    height: ", ";\n    ", ";\n  "])), sysSizeCircleMd, sysSizeCircleMd, body({
    lineHeight: sysSizeCircleMd
  }));
  var sizeMd = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: size_md;\n    width: ", ";\n    height: ", ";\n    ", "\n  "])), sysSizeCircleLg, sysSizeCircleLg, headingSm({
    lineHeight: sysSizeCircleLg
  }));
  var sizeLg = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: size_lg;\n    ", "\n    width: ", ";\n    height: ", ";\n  "])), headingLg({
    lineHeight: sysSizeCircleXl
  }), sysSizeCircleXl, sysSizeCircleXl);
  var getColorCss = function (colorName) {
    var _getColorFamily;
    var backgroundColor = getColor(colorName, acceptedColors, sysColorAccent1Main);
    var color = acceptedColors[colorName] ? (_getColorFamily = getColorFamily(colorName, legalColors)) === null || _getColorFamily === void 0 ? void 0 : _getColorFamily.contrast : sysColorAccent1Contrast;
    return css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      label: ", ";\n      background-color: ", ";\n      color: ", ";\n    "])), colorName, backgroundColor, color);
  };
  var getGenericColorCss = function (generic) {
    var genericCategory = {
      bank: {
        backgroundColor: sysColorAccent3Main,
        color: sysColorAccent3Contrast
      },
      causes: {
        backgroundColor: sysColorAccent4Main,
        color: sysColorAccent4Contrast
      },
      biller: {
        backgroundColor: sysColorAccent4Main,
        color: sysColorAccent4Contrast
      },
      merchant: {
        backgroundColor: sysColorAccent1Main,
        color: sysColorAccent1Contrast
      },
      profile: {
        backgroundColor: sysColorAccent1Contrast,
        color: sysColorAccent1Main
      },
      subscription: {
        backgroundColor: sysColorAccent2Main,
        color: sysColorAccent2Contrast
      }
    };
    return css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      label: ", ";\n      background-color: ", ";\n      color: ", ";\n    "])), generic, genericCategory[generic].backgroundColor, genericCategory[generic].color);
  };
  var base = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: avatar_base;\n    border-radius: ", ";\n    color: ", ";\n    text-align: center;\n    text-transform: uppercase;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    border: ", " solid ", ";\n    background-color: ", ";\n    position: relative;\n    ", ";\n  "])), sysBorderRadiusInteractive, sysColorAccent1Contrast, sysBorderThicknessNormal, sysColorBackgroundMain, sysColorAccent1Main, sizeLg);
  var image = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: image;\n    border-radius: inherit;\n    width: inherit;\n    height: inherit;\n    background: ", ";\n    object-fit: cover;\n  "])), sysColorAccent1Contrast);
  var avatarBadge = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: avatar_badge;\n    box-sizing: border-box;\n    position: absolute;\n    bottom: 0;\n    right: -", "; // Avatar has its own border of 1px, so shift the badge by 1px\n  "])), sysBorderThicknessNormal);
  var shimmer = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: shimmer;\n    ", "\n  "])), shimmerStyles.defaults({
    borderRadius: sysBorderRadiusInteractive,
    width: sysSizeCircleXl,
    height: sysSizeCircleXl
  }));
  var shimmerSizeXs = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: shimmer_size_xs;\n    width: ", ";\n    height: ", ";\n  "])), sysSizeCircleSm, sysSizeCircleSm);
  var shimmerSizeSm = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: shimmer_size_sm;\n    width: ", ";\n    height: ", ";\n  "])), sysSizeCircleMd, sysSizeCircleMd);
  var shimmerSizeMd = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    label: shimmer_size_md;\n    width: ", ";\n    height: ", ";\n  "])), sysSizeCircleLg, sysSizeCircleLg);
  return {
    base: base,
    image: image,
    sizeXs: sizeXs,
    sizeSm: sizeSm,
    sizeMd: sizeMd,
    sizeLg: sizeLg,
    getColorCss: getColorCss,
    getGenericColorCss: getGenericColorCss,
    avatarBadge: avatarBadge,
    shimmer: shimmer,
    shimmerSizeXs: shimmerSizeXs,
    shimmerSizeSm: shimmerSizeSm,
    shimmerSizeMd: shimmerSizeMd,
    acceptedColors: acceptedColors
  };
}
var _excluded = ["className", "color", "size", "src", "alt", "initials", "loading", "svgIconComponent", "badge", "generic", "isLoading", "isInverse", "children"];
var iconSizeMap = {
  lg: 'xl',
  md: 'lg',
  sm: 'md',
  xs: 'sm'
};
var Avatar = function (_ref) {
  var _styles$getColorCss, _styles$getGenericCol;
  var className = _ref.className,
    color = _ref.color,
    size = _ref.size,
    src = _ref.src,
    alt = _ref.alt,
    initials = _ref.initials,
    loading = _ref.loading,
    svgIconComponent = _ref.svgIconComponent,
    badge = _ref.badge,
    generic = _ref.generic,
    isLoading = _ref.isLoading,
    isInverse = _ref.isInverse,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]); // colorMap for backward compatiablity

  var sizes = ['lg', 'md', 'sm', 'xs'];
  // Compute sizeVariable - sizeLg, sizeMd etc.
  var computedSizeVar = sizes.includes(size) && "size".concat(size.charAt(0).toUpperCase()).concat(size.charAt(1));
  if (isLoading) {
    var shimmerCSS = [_styles.shimmer,
    //@ts-ignore
    sizes.includes(size) && _styles[computedSizeVar]];
    return jsx(Shimmer, _extends({
      css: shimmerCSS,
      className: className,
      isInverse: isInverse,
      hasStyleDefaults: false
    }, rest, {
      "data-ppui": "true"
    }), children);
  }
  var computedColor = function getColor(colour) {
    if (generic || src) return null;else if (initials) {
      var sum = initials.charCodeAt(0) + (initials[1] ? initials === null || initials === void 0 ? void 0 : initials.charCodeAt(1) : 0); // istanbul ignore next

      var acceptedColors = _styles.acceptedColors ? Object.keys(_styles.acceptedColors) : '';
      var index = sum % acceptedColors.length;
      return acceptedColors[index];
    } else if (colour) {
      return colour;
    }
    return null;
  }({
    blue: 'sysColorAccent1Main',
    pink: 'sysColorAccent2Main',
    green: 'sysColorAccent3Main',
    purple: 'sysColorAccent4Main'
  }[color]) || color;
  var avatarCSS = [_styles.base,
  //@ts-ignore
  computedSizeVar && _styles[computedSizeVar],
  //@ts-ignore
  computedColor && ((_styles$getColorCss = _styles.getColorCss) === null || _styles$getColorCss === void 0 ? void 0 : _styles$getColorCss.call(_styles, computedColor)), ['profile', 'merchant', 'causes', 'bank', 'subscription', 'biller'].includes(generic) && ((_styles$getGenericCol = _styles.getGenericColorCss) === null || _styles$getGenericCol === void 0 ? void 0 : _styles$getGenericCol.call(_styles, generic))];
  var avatarBadge = badge && size !== 'xs' ? jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref2) {
    var css = _ref2.css;
    return React.cloneElement(badge, {
      className: css(_styles.avatarBadge)
    });
  }) : null;
  return jsx("div", _extends({
    className: className,
    css: avatarCSS,
    "data-ppui-info": "avatar_4.5.5"
  }, rest), function avatarContent() {
    if (src) {
      return jsx("img", {
        css: _styles.image,
        src: src,
        alt: alt,
        loading: loading,
        "data-ppui": "true"
      });
    }
    if (svgIconComponent) {
      return jsx(svgIconComponent, {
        size: iconSizeMap[size],
        "data-ppui": "true"
      });
    }
    if (generic) {
      var GenericIcon = {
        bank: BankIcon,
        causes: CausesIcon,
        merchant: MerchantIcon,
        profile: UserProfileIcon,
        biller: BillPaymentIcon,
        subscription: RecurrentIcon
      }[generic];
      /* istanbul ignore next */

      if (GenericIcon) {
        return (
          // @ts-ignore
          jsx(GenericIcon, {
            name: generic,
            size: iconSizeMap[size],
            "data-ppui": "true"
          })
        );
      }
    }
    return initials;
  }(), avatarBadge);
};
var _templateObject$1, _templateObject2$1, _templateObject3$1;
function styles$1(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysColorBackgroundMain = tokens.sysColorBackgroundMain,
    sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
    sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
    sysSpaceSm = tokens.sysSpaceSm;
  var ppuiAvatarStackSpacing = sysSpaceSm;
  var _utilStyles = utilStyles(tokens),
    ellipsis = _utilStyles.ellipsis,
    screenReader = _utilStyles.screenReader;
  var _textStyles = textStyles(tokens),
    caption = _textStyles.caption;
  var base = css(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n    label: avatar_stack_base;\n    display: flex;\n    align-items: center;\n  "])));
  var avatar = css(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n    label: avatar;\n    border-radius: ", ";\n    flex-shrink: 0;\n    background-color: ", ";\n    z-index: 1;\n    &:not(:first-of-type) {\n      margin-left: -", ";\n    }\n    [dir='rtl'] & {\n      margin-left: 0;\n      &:not(:first-of-type) {\n        margin-right: -", ";\n      }\n    }\n    & div {\n      overflow: hidden;\n    }\n  "])), sysBorderRadiusInteractive, sysColorBackgroundMain, ppuiAvatarStackSpacing, ppuiAvatarStackSpacing);
  var label = css(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n    label: label;\n    ", ";\n    margin-left: ", ";\n    margin-right: 0;\n    ", ";\n    [dir='rtl'] & {\n      margin-left: 0;\n      margin-right: ", ";\n    }\n  "])), caption({
    color: sysColorBackgroundMediumContrast
  }), sysSpaceSm, ellipsis, sysSpaceSm);
  return {
    base: base,
    avatar: avatar,
    screenReader: screenReader,
    label: label
  };
}
var PREFIX_COMPONENT = 'avatarStacked';
var DISPLAY_LIMIT_STACK = 3;
var _excluded$1 = ["className", "size", "label", "isLabelHidden", "remainingCount", "children"];
var renderChildren = function (children, size, _styles) {
  var avatarComponents = []; // Only take specified items for displaying

  children = Array.isArray(children) && children.length > DISPLAY_LIMIT_STACK ? children.slice(0, DISPLAY_LIMIT_STACK) : children;
  React.Children.forEach(children, function (child, index) {
    var avatar = jsx(ClassNames, {
      key: "".concat(PREFIX_COMPONENT, "__").concat(index),
      "data-ppui": "true"
    }, function (_ref) {
      var css = _ref.css;
      return jsx("div", {
        className: css(_styles.avatar),
        "data-ppui": "true"
      }, React.cloneElement(child, {
        size: size
      }));
    });
    avatarComponents.push(avatar);
  });
  return avatarComponents;
};
var AvatarStack = function (_ref2) {
  var className = _ref2.className,
    size = _ref2.size,
    _ref2$label = _ref2.label,
    label = _ref2$label === void 0 ? 'default' : _ref2$label,
    isLabelHidden = _ref2.isLabelHidden,
    remainingCount = _ref2.remainingCount,
    children = _ref2.children,
    rest = _objectWithoutProperties(_ref2, _excluded$1);
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles$1(tokens);
  }, [tokens]);
  var labelStyles = [_styles.label, isLabelHidden && _styles.screenReader];
  size = 'xs'; // only `xs` size is allowed

  return jsx("div", _extends({
    className: className,
    css: _styles.base,
    "data-ppui-info": "avatar-stack_4.5.5"
  }, rest), renderChildren(children, size, _styles), jsx("span", {
    css: labelStyles,
    "data-ppui": "true"
  }, label));
};
var _templateObject$2, _templateObject2$2;
function styles$2(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    // eslint-disable-next-line no-console
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysSpaceMd = tokens.sysSpaceMd,
    sysSpaceSm = tokens.sysSpaceSm,
    sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast;
  var _textStyles = textStyles(tokens),
    caption = _textStyles.caption;
  var _utilStyles = utilStyles(tokens),
    focusStyles = _utilStyles.focusStyles;
  var base = css(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n    label: avatar_button_base;\n    position: relative;\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    padding: ", " 0;\n    min-height: ", ";\n    width: ", ";\n    word-wrap: break-word;\n    background: none;\n    border: none;\n    ", "\n\n    &:focus, &:hover {\n      cursor: pointer;\n    }\n\n    &:focus > span:first-of-type,\n    &:hover > span:first-of-type {\n      -webkit-text-stroke-width: ", ";\n    }\n\n    &:hover {\n      span:last-of-type {\n        text-decoration: underline;\n      }\n    }\n\n    &:focus {\n      outline: none;\n      & > div:first-of-type {\n        ", "\n      }\n      span:last-of-type {\n        text-decoration: underline;\n      }\n    }\n  "])), sysSpaceMd, '7.25rem', '6rem', caption({
    color: sysColorBackgroundHighContrast
  }), '0.0421875rem', focusStyles.focusElement);
  var label = css(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n    label: avatar_button_label;\n    text-align: center;\n    padding: ", " ", " 0 ", ";\n    width: inherit;\n    display: -webkit-box;\n    -webkit-line-clamp: 2;\n    -webkit-box-orient: vertical;\n    overflow: hidden;\n  "])), sysSpaceSm, sysSpaceSm, sysSpaceSm);
  return {
    base: base,
    label: label
  };
}
var _excluded$2 = ["className", "label", "children", "as"];
var AvatarButton = forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'default' : _ref$label,
    children = _ref.children,
    as = _ref.as,
    rest = _objectWithoutProperties(_ref, _excluded$2);
  var tokens = useTheme(); // Fetch tokens from Emotion context

  var _styles = useMemo(function () {
    return styles$2(tokens);
  }, [tokens]); // Avatar size fixed to 'md' for Avatar Button

  var avatar = React.cloneElement(children, {
    size: 'md',
    'aria-hidden': 'true'
  });
  return jsx(as || 'button', _extends({
    role: "button",
    css: _styles.base,
    className: className,
    tabIndex: 0,
    ref: ref
  }, rest, {
    "data-ppui": "true"
  }), avatar, jsx("span", {
    css: _styles.label,
    "data-ppui": "true"
  }, label));
});
export { Avatar, AvatarButton, AvatarStack, styles$2 as avatarButtonStyles, styles$1 as avatarStackStyles, styles as avatarStyles };